import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';

// TODO: Remove these after testing is complete
const TEST_ADMIN_EMAIL = 'admin@dayclap.com';
const TEST_ADMIN_PASSWORD = 'Para5588';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, isAdmin, loading: authLoading } = useContext(AuthContext);
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!authLoading && isAuthenticated && isAdmin) {
      navigate('/admin', { replace: true });
    }
  }, [isAuthenticated, isAdmin, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.email || !form.password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    const result = await login(form.email, form.password);
    setLoading(false);

    if (result.success) {
      if (result.role === 'admin') {
        setTimeout(() => {
          navigate('/admin', { replace: true });
        }, 100);
      } else {
        setError('Access denied. This login is for administrators only.');
      }
    } else {
      setError(result.error || 'Invalid credentials');
    }
  };

  // TODO: Remove autofill after testing
  const handleAutofill = () => {
    setForm({ email: TEST_ADMIN_EMAIL, password: TEST_ADMIN_PASSWORD });
  };

  return (
    <div className="min-h-screen w-full bg-slate-900 flex items-center justify-center p-6">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img
                src="https://cloud.sharemyimage.com/2026/08/16/DayClap-Logo.png"
                alt="DayClap"
                className="h-24"
              />
              <span className="text-xs font-bold text-white bg-slate-800 px-2.5 py-1 rounded-full">
                ADMIN
              </span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Admin Portal</h1>
            <p className="text-sm text-gray-500">Sign in to manage your platform</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <SafeIcon name="Mail" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Enter your email"
                  autoComplete="email"
                  className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-gray-200 focus:border-slate-500 focus:ring-2 focus:ring-slate-100 outline-none transition-all text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <SafeIcon name="Lock" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  className="w-full pl-12 pr-12 py-3.5 rounded-xl border border-gray-200 focus:border-slate-500 focus:ring-2 focus:ring-slate-100 outline-none transition-all text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <SafeIcon name={showPassword ? 'EyeOff' : 'Eye'} />
                </button>
              </div>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-100 text-red-600 text-sm p-3 rounded-xl flex items-center gap-2"
              >
                <SafeIcon name="AlertCircle" className="flex-shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 text-white font-bold py-3.5 rounded-xl hover:bg-slate-800 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <SafeIcon name="Shield" className="text-sm" />
                  Sign In to Admin
                </>
              )}
            </button>
          </form>

          {/* TODO: Remove testing autofill button after testing is complete */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={handleAutofill}
              className="w-full text-xs text-gray-400 hover:text-slate-600 transition-colors py-2 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-1.5"
            >
              <SafeIcon name="Zap" className="text-xs" />
              Testing Mode — Autofill Credentials
            </button>
          </div>

          <div className="mt-4 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1 mx-auto"
            >
              <SafeIcon name="ArrowLeft" className="text-xs" />
              Back to DayClap
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">
          <SafeIcon name="Lock" className="inline text-xs mr-1" />
          Authorized personnel only. All access is logged.
        </p>
      </motion.div>
    </div>
  );
};

export default AdminLogin;